/**
 * Multi movie ticket cart.
 * Each item holds an already reserved (seat-hold) show, so that the cashier
 * can sell tickets of multiple movies/sessions in a single POS transaction.
 *
 * Item shape:
 * {
 *   reservation_id, sd (encrypted show info for offers),
 *   ss_id, md_id, screen_id,
 *   movie_title, lang_name, mf_name, rating,
 *   ss_start_date, ss_start_show_time, screen_name,
 *   seats: ["A1", "A2"],
 *   total_amount (gross incl. taxes/addons)
 * }
 */
const STORAGE_KEY = "pos_ticket_cart";

const loadFromStorage = () => {
  try {
    // Drop any leftover cart from the old localStorage persistence so it
    // cannot resurface if something still reads that key.
    window.localStorage.removeItem(STORAGE_KEY);
    // sessionStorage is per-tab, so a second POS tab won't inherit another
    // cashier's multi-movie cart.
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
};

const saveToStorage = (items) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items || []));
  } catch (err) {
    console.log("TicketCart storage error:", err);
  }
};

const state = {
  items: loadFromStorage(),
};

const mutations = {
  ADD_TICKET_CART_ITEM: (state, payload) => {
    const index = state.items.findIndex(
      (x) => String(x.reservation_id) === String(payload.reservation_id)
    );
    if (index >= 0) {
      state.items.splice(index, 1, payload);
    } else {
      state.items.push(payload);
    }
    saveToStorage(state.items);
  },
  REMOVE_TICKET_CART_ITEM: (state, reservation_id) => {
    state.items = state.items.filter(
      (x) => String(x.reservation_id) !== String(reservation_id)
    );
    saveToStorage(state.items);
  },
  CLEAR_TICKET_CART: (state) => {
    state.items = [];
    saveToStorage(state.items);
  },
};

const actions = {
  addTicketCartItem: ({ commit }, payload) => {
    commit("ADD_TICKET_CART_ITEM", payload);
  },
  removeTicketCartItem: ({ commit }, reservation_id) => {
    commit("REMOVE_TICKET_CART_ITEM", reservation_id);
  },
  clearTicketCart: ({ commit }) => {
    commit("CLEAR_TICKET_CART");
  },
};

const getters = {
  GET_TICKET_CART_ITEMS: (state) => state.items,
  HAS_TICKET_CART_ITEMS: (state) =>
    Array.isArray(state.items) && state.items.length > 0,
  TICKET_CART_TOTAL_AMOUNT: (state) =>
    state.items.reduce(
      (sum, item) => sum + parseFloat(item.total_amount || 0),
      0
    ),
};

export default {
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/store/modules/TicketCart.js